import { useCallback, useMemo, useState } from 'react';
import toast from 'react-hot-toast';

import { Gate } from '../../../global/contexts/auth/auth';
import { avatarTone, initialsOf } from '../../../common/libs/initials';
import Picker from '../../../common/components/picker/picker';
// Cross-feature import, on purpose: the users search is knowledge that belongs
// to the users feature, and a second copy here is a second place to fix the
// day that path changes.
import { searchUsers } from '../../users/contexts/users';
import { useAddTeamMember, useRemoveTeamMember, useTeamMembers } from '../contexts/teams';
import { TEAM_ROLE_OPTIONS } from '../utils/form';

import '../styles/members.css';

const TEAM_ROLE_LABEL = Object.fromEntries(
    TEAM_ROLE_OPTIONS.map(option => [option.value, option.label]),
);

export default function TeamMembers({ teamId }) {
    const { data, isLoading, error } = useTeamMembers(teamId);

    const addMember = useAddTeamMember(teamId);
    const removeMember = useRemoveTeamMember(teamId);

    const [userId, setUserId] = useState('');
    const [teamRole, setTeamRole] = useState('member');

    // Memoised and not `data?.members ?? []` inline: that literal is a new
    // array on every render, and it feeds the useMemo below — which would then
    // recompute forever.
    const members = useMemo(() => data?.members ?? [], [data]);

    // Whoever is already in the team is filtered out of the search: adding them
    // twice answers 409, and offering a choice that always fails is a trap.
    // The filter runs on the RESULTS and not on a page of a hundred, so it
    // holds however many people exist.
    const taken = useMemo(
        () => new Set(members.map(member => member.user_id)),
        [members],
    );

    const findPeople = useCallback(
        (q, signal) => searchUsers(q, signal).then(items => items.filter(u => !taken.has(u.id))),
        [taken],
    );

    const handleAdd = async (e) => {
        e.preventDefault();
        if (!userId) return;
        try {
            await addMember.mutateAsync({ user_id: Number(userId), team_role: teamRole });
            setUserId('');
            setTeamRole('member');
            toast.success('Persona agregada al equipo');
        } catch (err) {
            toast.error(err.message || 'No se pudo agregar');
        }
    };

    const handleRemove = async (member) => {
        try {
            await removeMember.mutateAsync(member.user_id);
            toast.success('Persona quitada del equipo');
        } catch (err) {
            toast.error(err.message || 'No se pudo quitar');
        }
    };

    if (isLoading) return <p className="members__loading">Cargando miembros...</p>;
    if (error) {
        return (
            <p className="members__error">
                {error.message || 'No se pudieron cargar los miembros'}
            </p>
        );
    }

    return (
        <section className="members">
            <header className="members__header">
                <h3 className="members__title">Miembros</h3>
                <span className="members__count">{members.length}</span>
            </header>

            {!members.length && (
                <p className="members__empty">Este equipo todavía no tiene a nadie.</p>
            )}

            <ul className="members__list">
                {members.map(member => (
                    <li className="member" key={member.id}>
                        <span
                            className={`avatar member__avatar avatar--tone-${avatarTone(member.user?.name || member.user?.email)}`}
                            aria-hidden="true"
                        >
                            {initialsOf(member.user?.name || member.user?.email)}
                        </span>

                        <span className="member__identity">
                            <span className="member__name">
                                {member.user?.name || `Usuario #${member.user_id}`}
                            </span>
                            <span className="member__email">{member.user?.email}</span>
                        </span>

                        <span className="member__role">
                            {TEAM_ROLE_LABEL[member.team_role] ?? member.team_role}
                        </span>

                        <Gate domain="teams:write">
                            <button
                                type="button"
                                className="member__remove"
                                onClick={() => handleRemove(member)}
                                disabled={removeMember.isPending}
                            >
                                Quitar
                            </button>
                        </Gate>
                    </li>
                ))}
            </ul>

            <Gate domain="teams:write">
                {/* Not a <form>: this sits inside the team's form, and nesting
                    one form in another is invalid HTML — the browser drops the
                    inner one and the button ends up saving the team instead. */}
                <div className="members__add">
                    <span className="members__picker">
                        <Picker
                            value={userId}
                            onChange={setUserId}
                            fetchOptions={findPeople}
                            cacheKey={`team-${teamId}-candidates`}
                            labelOf={user => user.name}
                            subtitleOf={user => user.email}
                            placeholder="Elegir persona..."
                            disabled={addMember.isPending}
                        />
                    </span>

                    <select
                        className="members__select members__select--role"
                        value={teamRole}
                        onChange={e => setTeamRole(e.target.value)}
                        disabled={addMember.isPending}
                    >
                        {TEAM_ROLE_OPTIONS.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>

                    <button
                        type="button"
                        className="members__add-button"
                        onClick={handleAdd}
                        disabled={!userId || addMember.isPending}
                    >
                        {addMember.isPending ? 'Agregando...' : 'Agregar'}
                    </button>
                </div>
            </Gate>
        </section>
    );
}
